import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { clearLocationFilters } from 'store/reducers/filter/filterReducer'

import type { Locality } from './locality'
import {
  isLocalityRoute,
  LOCALITY_COOKIE,
  localityFromQuery,
  localityQuery,
  locationChangeQuery,
  parseLocality,
} from './locality'

const LocalityContext = createContext<{
  locality: Locality | null
  chooseLocality: (value: Locality | null) => Promise<void>
}>({ locality: null, chooseLocality: async () => {} })

export const LocalityProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const [locality, setLocality] = useState<Locality | null>(null)
  const current = useRef<Locality | null>(null)

  const save = useCallback((value: Locality | null) => {
    current.current = value
    setLocality(value)
    window.localStorage.setItem('location', JSON.stringify(value))
    // Без domain: отдельный выбор для локальной среды и для прода.
    Cookies.set(LOCALITY_COOKIE, JSON.stringify(value), {
      expires: 365,
      sameSite: 'lax',
    })
  }, [])

  useEffect(() => {
    if (!router.isReady) return
    const saved =
      parseLocality(Cookies.get(LOCALITY_COOKIE)) ||
      parseLocality(window.localStorage.getItem('location') || undefined)
    if (isLocalityRoute(router.pathname) && 'localityGUID' in router.query) {
      const selected = localityFromQuery(router.query, saved)
      if (selected?.localityGUID !== current.current?.localityGUID) {
        dispatch(clearLocationFilters())
      }
      save(selected)
    } else {
      save(saved)
      if (saved && isLocalityRoute(router.pathname)) {
        router.replace({
          pathname: router.pathname,
          query: { ...router.query, ...localityQuery(saved) },
        })
      }
    }
    // Изменение URL (включая Назад/Вперёд) — источник выбора на страницах выдачи.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.asPath, save])

  const chooseLocality = useCallback(
    async (value: Locality | null) => {
      const previous = current.current
      save(value)
      dispatch(clearLocationFilters())
      if (isLocalityRoute(router.pathname)) {
        try {
          await router.push({
            pathname: router.pathname,
            query: locationChangeQuery(router.query, value),
          })
        } catch (error) {
          save(previous)
          throw error
        }
      }
    },
    [router, save, dispatch],
  )

  const context = useMemo(
    () => ({ locality, chooseLocality }),
    [locality, chooseLocality],
  )
  return (
    <LocalityContext.Provider value={context}>
      {children}
    </LocalityContext.Provider>
  )
}

export const useLocality = () => useContext(LocalityContext)
